import { useEffect, useMemo, useRef, useState, type FC, type KeyboardEvent } from 'react';
import { Button, Empty, Input, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { projectApi } from '../api/services';
import type {
  ProjectOperationLogItemVO,
  ProjectOperationLogQueryParams,
} from '../api/types';
import {
  IpcDateRangePicker,
  IpcFilterToolbar,
  IpcTable,
  IPC_FILTER_MULTI_SELECT_PROPS,
  type IpcFilterChipItem,
} from './ui';

type Props = {
  projectId: number | null;
  /** 仅在「操作日志」Tab 激活时拉取；与排期主数据请求完全解耦 */
  active: boolean;
};

type FilterState = {
  dateRange: [string | undefined, string | undefined] | null;
  operationTypes: string[];
  /** 操作人模糊关键字 */
  operatorKeyword: string;
  objectTypes: string[];
  objectKeyword: string;
  changeKeyword: string;
};

const OPERATION_TYPE_OPTIONS = [
  { value: '新增', label: '新增' },
  { value: '更新', label: '更新' },
  { value: '删除', label: '删除' },
];

const OBJECT_TYPE_OPTIONS = [
  { value: '项目', label: '项目' },
  { value: '排期版本', label: '排期版本' },
  { value: '规格', label: '规格' },
  { value: '节点', label: '节点' },
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

/** 最近七天：含当天共 7 个自然日，精确到时分秒 */
function defaultLast7Days(): [string, string] {
  const end = dayjs().endOf('day');
  const start = end.subtract(6, 'day').startOf('day');
  return [start.format('YYYY-MM-DD HH:mm:ss'), end.format('YYYY-MM-DD HH:mm:ss')];
}

function createDefaultFilters(): FilterState {
  const [start, end] = defaultLast7Days();
  return {
    dateRange: [start, end],
    operationTypes: [],
    operatorKeyword: '',
    objectTypes: [],
    objectKeyword: '',
    changeKeyword: '',
  };
}

function toQueryParams(f: FilterState): ProjectOperationLogQueryParams {
  const start = f.dateRange?.[0]?.trim() || undefined;
  const end = f.dateRange?.[1]?.trim() || undefined;
  return {
    startTime: start || undefined,
    endTime: end || undefined,
    operationTypes: f.operationTypes.length ? f.operationTypes.join(',') : undefined,
    operators: f.operatorKeyword.trim() || undefined,
    objectTypes: f.objectTypes.length ? f.objectTypes.join(',') : undefined,
    objectKeyword: f.objectKeyword.trim() || undefined,
    changeKeyword: f.changeKeyword.trim() || undefined,
  };
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const name = (err as { name?: string }).name;
  return name === 'AbortError';
}

export const ScheduleOperationLogTab: FC<Props> = ({ projectId, active }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ProjectOperationLogItemVO[]>([]);
  const [draft, setDraft] = useState<FilterState>(() => createDefaultFilters());
  const [applied, setApplied] = useState<FilterState>(() => createDefaultFilters());
  const [retryKey, setRetryKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const loadedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    loadedKeyRef.current = null;
    setRows([]);
    setError(null);
    setPage(1);
    const next = createDefaultFilters();
    setDraft(next);
    setApplied(next);
  }, [projectId]);

  const appliedQueryKey = useMemo(
    () => JSON.stringify(toQueryParams(applied)),
    [applied],
  );

  useEffect(() => {
    setPage(1);
  }, [appliedQueryKey]);

  useEffect(() => {
    if (!active || !projectId || projectId <= 0) return;
    const cacheKey = `${projectId}|${appliedQueryKey}`;
    if (loadedKeyRef.current === cacheKey) return;

    let cancelled = false;
    const pid = projectId;
    const params = toQueryParams(applied);
    const ac = new AbortController();
    setLoading(true);
    setError(null);

    projectApi
      .getOperationLog(pid, params, ac.signal)
      .then(result => {
        if (cancelled) return;
        setRows(result.items);
        loadedKeyRef.current = cacheKey;
      })
      .catch(err => {
        if (cancelled || isAbortError(err)) return;
        console.error('加载操作日志失败', err);
        setRows([]);
        setError(err instanceof Error ? err.message : '加载操作日志失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [active, projectId, appliedQueryKey, retryKey, applied]);

  const handleSubmit = () => {
    loadedKeyRef.current = null;
    setApplied({ ...draft, dateRange: draft.dateRange ? [...draft.dateRange] as [string | undefined, string | undefined] : null });
  };

  /** 仅物理回车触发查询；中文输入法回车上屏（isComposing）不查 */
  const handleFilterPressEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    const ne = e.nativeEvent;
    if (ne.isComposing || ne.keyCode === 229) return;
    handleSubmit();
  };

  const handleReset = () => {
    const next = createDefaultFilters();
    loadedKeyRef.current = null;
    setDraft(next);
    setApplied(next);
  };

  const chipItems = useMemo((): IpcFilterChipItem[] => {
    const chips: IpcFilterChipItem[] = [];
    const start = applied.dateRange?.[0];
    const end = applied.dateRange?.[1];
    if (start || end) {
      chips.push({ key: 'date', label: `事件时间: ${start || '…'} ~ ${end || '…'}` });
    }
    if (applied.operationTypes.length) {
      chips.push({ key: 'types', label: `类型: ${applied.operationTypes.join('、')}` });
    }
    if (applied.operatorKeyword.trim()) {
      chips.push({ key: 'operators', label: `操作人: ${applied.operatorKeyword.trim()}` });
    }
    if (applied.objectTypes.length) {
      chips.push({ key: 'objectTypes', label: `对象类型: ${applied.objectTypes.join('、')}` });
    }
    if (applied.objectKeyword.trim()) {
      chips.push({ key: 'objectKeyword', label: `操作对象: ${applied.objectKeyword.trim()}` });
    }
    if (applied.changeKeyword.trim()) {
      chips.push({ key: 'changeKeyword', label: `变更内容: ${applied.changeKeyword.trim()}` });
    }
    return chips;
  }, [applied]);

  const removeChip = (key: string) => {
    const nextDraft = { ...draft };
    const nextApplied = { ...applied };
    switch (key) {
      case 'date':
        nextDraft.dateRange = null;
        nextApplied.dateRange = null;
        break;
      case 'types':
        nextDraft.operationTypes = [];
        nextApplied.operationTypes = [];
        break;
      case 'operators':
        nextDraft.operatorKeyword = '';
        nextApplied.operatorKeyword = '';
        break;
      case 'objectTypes':
        nextDraft.objectTypes = [];
        nextApplied.objectTypes = [];
        break;
      case 'objectKeyword':
        nextDraft.objectKeyword = '';
        nextApplied.objectKeyword = '';
        break;
      case 'changeKeyword':
        nextDraft.changeKeyword = '';
        nextApplied.changeKeyword = '';
        break;
      default:
        return;
    }
    loadedKeyRef.current = null;
    setDraft(nextDraft);
    setApplied(nextApplied);
  };

  const clearAllChips = () => {
    const next: FilterState = {
      dateRange: null,
      operationTypes: [],
      operatorKeyword: '',
      objectTypes: [],
      objectKeyword: '',
      changeKeyword: '',
    };
    loadedKeyRef.current = null;
    setDraft(next);
    setApplied(next);
  };

  const handleRetry = () => {
    loadedKeyRef.current = null;
    setError(null);
    setRetryKey(k => k + 1);
  };

  const pagedRows = useMemo(() => {
    const maxPage = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
    const safePage = Math.min(page, maxPage);
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [rows.length, pageSize, page]);

  const columns: ColumnsType<ProjectOperationLogItemVO> = [
    {
      title: '操作时间',
      dataIndex: 'operation_time',
      key: 'operation_time',
      width: 168,
    },
    {
      title: '操作类型',
      dataIndex: 'operation_type',
      key: 'operation_type',
      width: 88,
      render: v => ((v as string) || '').trim() || '—',
    },
    {
      title: '操作人',
      dataIndex: 'operator_name',
      key: 'operator_name',
      width: 96,
      render: v => v || '—',
    },
    {
      title: '操作对象',
      key: 'object',
      width: 200,
      render: (_, row) => {
        const title = row.object_title || '—';
        const subtitle = row.object_subtitle || '';
        return (
          <div className="schedule-oplog-object">
            <div className="schedule-oplog-object__title">{title}</div>
            {subtitle ? (
              <div className="schedule-oplog-object__subtitle">{subtitle}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: '变更内容',
      dataIndex: 'change_content',
      key: 'change_content',
      render: v => {
        const text = (v as string) || '—';
        return <div className="schedule-oplog-change">{text}</div>;
      },
    },
  ];

  return (
    <div className="schedule-oplog-tab">
      <div className="ipc-table-viewport schedule-oplog-viewport">
        <div className="ipc-table-viewport__top">
          <IpcFilterToolbar
            className="schedule-oplog-filter"
            onSubmit={handleSubmit}
            onReset={handleReset}
            chipItems={chipItems}
            onRemoveChip={removeChip}
            onClearChips={clearAllChips}
          >
            <IpcDateRangePicker
              variant="filter"
              showTime
              value={draft.dateRange ?? [undefined, undefined]}
              onChange={([start, end]) => {
                if (!start && !end) {
                  setDraft(prev => ({ ...prev, dateRange: null }));
                  return;
                }
                setDraft(prev => ({ ...prev, dateRange: [start, end] }));
              }}
              className="ipc-filter-bar__field ipc-filter-bar__field--range schedule-oplog-filter__datetime"
              placeholder={['事件开始', '事件结束']}
              allowClear
            />
            <Select
              mode="multiple"
              placeholder="类型"
              value={draft.operationTypes}
              onChange={values => setDraft(prev => ({ ...prev, operationTypes: values ?? [] }))}
              allowClear
              optionFilterProp="label"
              {...IPC_FILTER_MULTI_SELECT_PROPS}
              className="ipc-filter-bar__field schedule-oplog-filter__select-sm"
              options={OPERATION_TYPE_OPTIONS}
            />
            <Input
              placeholder="操作人"
              value={draft.operatorKeyword}
              onChange={e => setDraft(prev => ({ ...prev, operatorKeyword: e.target.value }))}
              onPressEnter={handleFilterPressEnter}
              allowClear
              className="ipc-filter-bar__field"
            />
            <Select
              mode="multiple"
              placeholder="对象类型"
              value={draft.objectTypes}
              onChange={values => setDraft(prev => ({ ...prev, objectTypes: values ?? [] }))}
              allowClear
              optionFilterProp="label"
              {...IPC_FILTER_MULTI_SELECT_PROPS}
              className="ipc-filter-bar__field schedule-oplog-filter__select-sm"
              options={OBJECT_TYPE_OPTIONS}
            />
            <Input
              placeholder="操作对象"
              value={draft.objectKeyword}
              onChange={e => setDraft(prev => ({ ...prev, objectKeyword: e.target.value }))}
              onPressEnter={handleFilterPressEnter}
              allowClear
              className="ipc-filter-bar__field"
            />
            <Input
              placeholder="变更内容"
              value={draft.changeKeyword}
              onChange={e => setDraft(prev => ({ ...prev, changeKeyword: e.target.value }))}
              onPressEnter={handleFilterPressEnter}
              allowClear
              className="ipc-filter-bar__field"
            />
          </IpcFilterToolbar>
        </div>

        <div className="ipc-table-viewport__table">
          {error ? (
            <div className="schedule-oplog-empty">
              <Empty description={error}>
                <Button type="primary" onClick={handleRetry}>
                  重新加载
                </Button>
              </Empty>
            </div>
          ) : (
            <IpcTable<ProjectOperationLogItemVO>
              loading={loading}
              rowKey={(r, i) =>
                `${r.transaction_id ?? ''}-${r.source_table ?? ''}-${r.source_id ?? ''}-${r.operation_time}-${page}-${i}`
              }
              columns={columns}
              dataSource={pagedRows}
              emptyTitle="暂无操作日志"
              pagination={{
                current: page,
                pageSize,
                total: rows.length,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                },
              }}
              size="middle"
              tableLayout="fixed"
              scroll={{ x: 1280 }}
              autoHeight
              autoHeightBottomGap={24}
              autoHeightDeps={[pagedRows.length, page, pageSize, loading, chipItems.length]}
            />
          )}
        </div>
      </div>
    </div>
  );
};
